import { ResponseGetClientsList } from '@/api/getClientsList';
import { create } from 'zustand';

type Client = {
  smerViewed: Set<string>;
  userName: string;
  smerIds: string[];
  daybooksIds: string[];
  daybooksViewed: Set<string>;
  shareWithPsySmer: boolean;
  shareWithPsyDaybook: boolean;
};

type User = {
  clients: Record<string, Client>;

  name?: string;
  email: string;
  img?: string;

  addSmerView: (clientId: string, newString: string) => void;
  addDaybookView: (clientId: string, newString: string) => void;
  setClientStrings: (data: ResponseGetClientsList) => void;

  removeImage: () => void;
  updatePsychologist: ({
    name,
    email,
    img,
  }: {
    name?: string;
    email?: string;
    img?: string;
  }) => void;
};

export const useUserStore = create<User>()(set => ({
  clients: {},
  name: undefined,
  email: '',
  img: undefined,
  clientsHasNew: new Set([]),

  updatePsychologist: ({
    name,
    email,
    img,
  }: {
    name?: string;
    email?: string;
    img?: string;
  }) =>
    set(state => ({
      ...state,
      name: name || state.name,
      email: email || state.email,
      img: img || state.img,
    })),

  removeImage: () => set(() => ({ img: undefined })),

  addSmerView: (clientId: string, newString: string) =>
    set(state => {
      return {
        clients: {
          ...state.clients,
          [clientId]: {
            ...state.clients[clientId],
            smerViewed: new Set([
              ...(state.clients[clientId]?.smerViewed || []),
              newString,
            ]),
          },
        },
      };
    }),

  addDaybookView: (clientId: string, newString: string) =>
    set(state => {
      return {
        clients: {
          ...state.clients,
          [clientId]: {
            ...state.clients[clientId],
            daybooksViewed: new Set([
              ...(state.clients[clientId]?.daybooksViewed || []),
              newString,
            ]),
          },
        },
      };
    }),

  setClientStrings: data =>
    set(() => {
      const clients = data.reduce((acc: Record<string, Client>, client) => {
        const viewedSet = new Set(client.viewed);

        acc[client.id] = {
          userName: client.userName,
          smerViewed: new Set(client.smerIds.filter(id => viewedSet.has(id))),
          smerIds: client.smerIds,
          daybooksIds: client.daybooksIds,
          daybooksViewed: new Set(
            client.daybooksIds.filter(id => viewedSet.has(id))
          ),
          shareWithPsySmer: client.shareWithPsySmer,
          shareWithPsyDaybook: client.shareWithPsyDaybook,
        };
        return acc;
      }, {});

      return { clients };
    }),
}));
